'use client';

import { ArrowUp, MessageCircle, X, Send } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export function FloatingActions() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showWelcomeNotification, setShowWelcomeNotification] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: 'Hi! Welcome to Leadbunker! How can I help you today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 300px
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Show welcome notification after 2 seconds
    const welcomeTimer = setTimeout(() => {
      setShowWelcomeNotification(true);
    }, 2000);

    // Auto-hide notification after 8 seconds
    const hideTimer = setTimeout(() => {
      setShowWelcomeNotification(false);
    }, 10000);

    return () => {
      clearTimeout(welcomeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    setShowWelcomeNotification(false); // Hide notification when chat is opened
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;

    // Add user message
    const newMessage: Message = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages([...messages, newMessage]);
    setInputMessage('');

    // Simulate bot response after a delay
    setTimeout(() => {
      const botResponse: Message = {
        id: messages.length + 2,
        text: getBotResponse(inputMessage),
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
    }, 1000);
  };

  const getBotResponse = (userMessage: string): string => {
    const msg = userMessage.toLowerCase();

    if (
      msg.includes('price') ||
      msg.includes('pricing') ||
      msg.includes('cost')
    ) {
      return 'Our pricing is credit-based! Check out our Pricing page for detailed information. We offer Starter, Professional, and Enterprise plans.';
    } else if (msg.includes('email') || msg.includes('extraction')) {
      return 'We offer Email Extraction, Phone Extraction, and Social Media Extraction services. Would you like to know more about any specific service?';
    } else if (msg.includes('help') || msg.includes('support')) {
      return "I'm here to help! You can ask me about our services, pricing, or how Leadbunker works. For detailed support, visit our Contact page.";
    } else if (msg.includes('hello') || msg.includes('hi')) {
      return 'Hello! How can I assist you with Leadbunker today?';
    } else {
      return 'Thanks for your message! For specific inquiries, please visit our Contact page or explore our Solutions to learn more about what we offer.';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Back to Top Button - Bottom Left */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 left-6 z-50 w-10 h-10 md:w-12 md:h-12 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5 group-hover:transform group-hover:-translate-y-1 transition-transform" />
        </button>
      )}

      {/* Chat Window */}
      {isChatOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 h-[500px] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-5">
          {/* Chat Header */}
          <div className="bg-linear-to-r from-[#10b981] to-[#059669] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Leadbunker Support</h3>
                <p className="text-xs text-white/80">
                  We&apos;re here to help!
                </p>
              </div>
            </div>
            <button
              onClick={toggleChat}
              className="w-8 h-8 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-secondary/20">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.sender === 'user'
                      ? 'bg-[#10b981] text-white rounded-br-sm'
                      : 'bg-background border border-border rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm">{message.text}</p>
                  <p
                    className={`text-xs mt-1 ${
                      message.sender === 'user'
                        ? 'text-white/70'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-background border-t border-border">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 rounded-full border border-border bg-secondary focus:outline-none focus:ring-2 focus:ring-[#10b981] transition-all"
              />
              <button
                onClick={handleSendMessage}
                disabled={inputMessage.trim() === ''}
                className="w-10 h-10 bg-[#10b981] hover:bg-[#059669] disabled:bg-secondary disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-all hover:scale-105"
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chatbot Button - Bottom Right */}
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 md:w-16 md:h-16 bg-linear-to-br from-[#10b981] to-[#059669] text-white rounded-full shadow-lg hover:shadow-2xl transition-all hover:scale-110 flex items-center justify-center group animate-pulse hover:animate-none ring-4 ring-[#10b981]/20 hover:ring-[#10b981]/40 backdrop-blur-sm"
        aria-label="Open chat"
      >
        {isChatOpen ? (
          <X className="w-6 h-6 md:w-7 md:h-7 transition-transform" />
        ) : (
          <MessageCircle className="w-6 h-6 md:w-7 md:h-7 group-hover:transform group-hover:rotate-12 transition-transform drop-shadow-md" />
        )}
      </button>

      {/* Welcome Notification */}
      {showWelcomeNotification && !isChatOpen && (
        <div className="fixed bottom-24 right-24 z-40 max-w-xs bg-background border-2 border-[#10b981] rounded-2xl shadow-2xl p-4 animate-in slide-in-from-right-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-linear-to-br from-[#10b981] to-[#059669] rounded-full flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-foreground">Need help?</h4>
                <button
                  onClick={() => setShowWelcomeNotification(false)}
                  className="w-5 h-5 hover:bg-secondary rounded-full flex items-center justify-center transition-colors shrink-0"
                  aria-label="Close notification"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Chat with us! We&apos;re here to answer your questions about
                Leadbunker.
              </p>
            </div>
          </div>
          {/* Arrow pointing to chat button */}
          <div className="absolute -right-2 bottom-8 w-4 h-4 bg-background border-r-2 border-b-2 border-[#10b981] transform -rotate-45"></div>
        </div>
      )}
    </>
  );
}
